import type { SessionService } from '@efdigital/ll-addon-auth';
import {
  buildFindUserRequest,
  type ManagementUser,
  parseFindUserResponse,
} from '@efdigital/ll-admin-v2/data/builders/users/find-user';
import type { LookupUser } from '@efdigital/ll-admin-v2/data/builders/users/lookup-users';
import type AbilityService from '@efdigital/ll-admin-v2/services/ability';
import type UserLookupService from '@efdigital/ll-admin-v2/services/user-lookup';
import Route from '@ember/routing/route';
import { service } from '@ember/service';

type Params = { user_id: string };

export type LibraryUsersUserModel = {
  userId: string;
  profile: ManagementUser | LookupUser | null;
  profileLevel: 'full' | 'basic';
  loadError?: string;
};

export default class LibraryUsersUserRoute extends Route<LibraryUsersUserModel> {
  @service declare ability: AbilityService;
  @service declare session: SessionService;
  @service declare userLookup: UserLookupService;

  async model(params: Params): Promise<LibraryUsersUserModel> {
    const userId = params.user_id;

    if (this.ability.canEdit('clients:users')) {
      try {
        const request = await buildFindUserRequest(userId, this.session);
        const response = await fetch(request);

        if (response.status === 404) {
          return {
            userId,
            profile: null,
            profileLevel: 'full',
            loadError: 'User not found.',
          };
        }

        if (!response.ok) {
          return {
            userId,
            profile: null,
            profileLevel: 'full',
            loadError: `Failed to load user: ${response.statusText}`,
          };
        }

        const profile = await parseFindUserResponse(response);
        return {
          userId,
          profile,
          profileLevel: 'full',
        };
      } catch (error) {
        return {
          userId,
          profile: null,
          profileLevel: 'full',
          loadError:
            error instanceof Error ? error.message : 'Failed to load user.',
        };
      }
    }

    const profile = await this.userLookup.lookupUser(userId);
    return {
      userId,
      profile,
      profileLevel: 'basic',
    };
  }
}
